import React from 'react';
import { AuditReport } from '../../types/api';

interface ReportListProps {
  reports: AuditReport[];
}

const ReportList: React.FC<ReportListProps> = ({ reports }) => {
  const getReportTypeIcon = (type: string) => {
    switch (type) {
      case 'hourly': return '⏰';
      case 'daily': return '📅';
      case 'weekly': return '📊';
      case 'monthly': return '📈';
      case 'adhoc': return '🔍';
      default: return '📋';
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const formatDuration = (startTime: number, endTime: number) => {
    const duration = endTime - startTime;
    const hours = Math.floor(duration / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days} day${days > 1 ? 's' : ''}`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    return `${Math.floor(duration / (1000 * 60))} minutes`;
  };

  const calculateSuccessRate = (report: AuditReport) => {
    if (report.totalTransactions === 0) return 0;
    return (report.successfulTransactions / report.totalTransactions) * 100;
  };

  const formatVolume = (volume: string) => {
    // Convert from wei/gwei to ETH by dividing by 1e18
    const num = parseFloat(volume) / 1e18;
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toFixed(2);
  };

  const downloadReport = (reportId: string) => {
    // In a real implementation, this would download the report
    console.log(`Downloading report ${reportId}`);
    // You could implement PDF generation or CSV export here
  };

  return (
    <div className="divide-y divide-gray-200">
      {reports.length === 0 ? (
        <div className="px-6 py-12 text-center">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No reports available</h3>
          <p className="text-gray-500">Generate your first audit report to see it here.</p>
        </div>
      ) : (
        reports.map((report) => (
          <div key={report.id} className="px-6 py-6 hover:bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <span className="text-2xl">{getReportTypeIcon(report.reportType)}</span>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {report.reportType.charAt(0).toUpperCase() + report.reportType.slice(1)} Report
                    </h3>
                    <p className="text-sm text-gray-500">
                      Generated on {formatDate(report.createdAt)}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  {/* Period */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Period</p>
                    <p className="text-sm text-gray-900">
                      {formatDuration(report.startTime, report.endTime)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(report.startTime).toLocaleDateString()} - {new Date(report.endTime).toLocaleDateString()}
                    </p>
                  </div>

                  {/* Transactions */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Transactions</p>
                    <p className="text-sm text-gray-900">
                      {report.totalTransactions.toLocaleString()} total
                    </p>
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-green-600">Success:</span>
                        <span>{report.successfulTransactions}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-red-600">Failed:</span>
                        <span>{report.failedTransactions}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-yellow-600">Pending:</span>
                        <span>{report.pendingTransactions}</span>
                      </div>
                    </div>
                  </div>

                  {/* Success Rate */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Success Rate</p>
                    <p className={`text-sm font-semibold ${
                      calculateSuccessRate(report) >= 95 ? 'text-green-600' :
                      calculateSuccessRate(report) >= 90 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {calculateSuccessRate(report).toFixed(1)}%
                    </p>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                      <div 
                        className={`h-1.5 rounded-full ${
                          calculateSuccessRate(report) >= 95 ? 'bg-green-500' :
                          calculateSuccessRate(report) >= 90 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${calculateSuccessRate(report)}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Volume */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Volume</p>
                    <p className="text-sm text-gray-900">
                      {formatVolume(report.totalVolume)} PFVS
                    </p>
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-blue-600">BSC→Ronin:</span>
                        <span>{formatVolume(report.bscToRoninVolume)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-600">Ronin→BSC:</span>
                        <span>{formatVolume(report.roninToBscVolume)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Performance Metrics */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500">Average Processing Time</p>
                      <p className="text-sm text-gray-900">
                        {report.averageProcessingTime < 60 
                          ? `${report.averageProcessingTime.toFixed(1)}s`
                          : `${(report.averageProcessingTime / 60).toFixed(1)}m`
                        }
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500">Max Processing Time</p>
                      <p className="text-sm text-gray-900">
                        {report.maxProcessingTime < 60 
                          ? `${report.maxProcessingTime.toFixed(1)}s`
                          : `${(report.maxProcessingTime / 60).toFixed(1)}m`
                        }
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500">Validators</p>
                      <p className="text-sm text-gray-900">
                        {report.validatorPerformance.length} active
                      </p>
                    </div>
                  </div>
                </div>

                {/* Validator Performance Summary */}
                {report.validatorPerformance.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs font-medium text-gray-500 mb-2">Top Validators</p>
                    <div className="flex flex-wrap gap-2">
                      {report.validatorPerformance
                        .sort((a, b) => b.successRate - a.successRate)
                        .slice(0, 3)
                        .map((validator, index) => (
                          <div key={index} className="flex items-center space-x-2 bg-gray-100 rounded-full px-3 py-1">
                            <span className="text-xs font-mono">
                              {validator.validator.slice(0, 6)}...
                            </span>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex-shrink-0 ml-6">
                <div className="flex flex-col space-y-2">
                  <button
                    onClick={() => downloadReport(report.id)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download
                  </button>
                  
                  <button className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                    <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View Details
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default ReportList; 